import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { PiInfo, PiMoney } from 'react-icons/pi';
import { DocumentType, PaymentMethod, TInvoice } from '../../types';
import DocumentsUpload from '../DocumentsUpload/DocumentsUpload';

interface PaymentConfirmationFormProps {
  invoice: TInvoice;
  opened: boolean;
  onClose: () => void;
  onSubmit?: (data: PaymentConfirmationData) => Promise<void>;
}

export interface PaymentConfirmationData {
  invoiceId: string;
  paymentMethod: PaymentMethod;
  transactionHash?: string;
  fileId?: string;
  notes?: string;
}

interface PaymentFormValues {
  paymentMethod: PaymentMethod;
  transactionHash: string;
  notes: string;
}

export function PaymentConfirmationForm({
  invoice,
  opened,
  onClose,
  onSubmit,
}: PaymentConfirmationFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<PaymentFormValues>({
    initialValues: {
      paymentMethod: invoice.paymentMethod as PaymentMethod,
      transactionHash: '',
      notes: '',
    },
    validate: {
      paymentMethod: (value) => (!value ? 'Payment method is required' : null),
      transactionHash: (value, values) => {
        if (values.paymentMethod === PaymentMethod.Cryptocurrency && !value) {
          return 'Transaction hash is required for cryptocurrency payments';
        }
        return null;
      },
    },
  });

  if (!invoice) {
    return null;
  }

  const requiresFile =
    form.values.paymentMethod !== PaymentMethod.Cryptocurrency;
  const requiresHash =
    form.values.paymentMethod === PaymentMethod.Cryptocurrency;

  const handleSubmit = async (values: PaymentFormValues, fileId?: string) => {
    setIsSubmitting(true);

    try {
      const data: PaymentConfirmationData = {
        invoiceId: invoice.id.toString(),
        paymentMethod: values.paymentMethod,
        notes: values.notes,
      };

      if (requiresHash) {
        data.transactionHash = values.transactionHash;
      }

      if (fileId) {
        data.fileId = fileId;
      }

      await onSubmit?.(data);

      notifications.show({
        message: 'Payment confirmation submitted successfully',
        color: 'green',
      });

      onClose();
      form.reset();
    } catch (_error) {
      notifications.show({
        message: 'Failed to submit payment confirmation',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <PiMoney size={18} />
          <Text size="md" fw={600}>
            Payment Confirmation
          </Text>
        </Group>
      }
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
        <Stack gap="md">
          <Alert color="blue" icon={<PiInfo />}>
            <Text size="sm">
              Please provide payment confirmation for invoice{' '}
              {invoice.invoiceNumber}. Once confirmed, the invoice status will
              be updated to "Confirmed by Client".
            </Text>
          </Alert>

          {requiresHash && (
            <Stack>
              <TextInput
                label="Transaction Hash"
                placeholder="Enter cryptocurrency transaction hash"
                {...form.getInputProps('transactionHash')}
                required
                styles={{ input: { fontFamily: 'monospace' } }}
                description="Provide the transaction hash from your cryptocurrency wallet"
              />
              <Button type="submit">Save</Button>
            </Stack>
          )}

          {requiresFile && (
            <div>
              <Text size="sm" fw={500} mb="xs">
                Upload Payment Confirmation
              </Text>
              <Text size="xs" c="dimmed" mb="sm">
                Upload a document that proves payment was made (receipt, bank
                statement, etc.)
              </Text>
              <Alert color="orange" p="xs" mb="sm">
                <Text size="xs">
                  After uploading the payment confirmation file, the system will
                  automatically update the invoice status to "Confirmed by
                  Client".
                </Text>
              </Alert>
              <DocumentsUpload
                type={DocumentType.PaymentConfirmation}
                pulseId={invoice.contactId}
                maxDocuments={1}
                onUploadSuccess={async (documents) => {
                  if (documents.length > 0) {
                    await handleSubmit(form.values, documents[0].id.toString());
                  }
                }}
              />
            </div>
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default PaymentConfirmationForm;
